import type { AppSummary } from './api';
import { readPref, writePref } from './prefs';

/**
 * How the instance list is divided into sections.
 *
 * `alpha` is the A-Z the list has always had. `customer` collects a customer's
 * sites together — `ovf-austin` through `ovf-sanantonio`, the seven
 * `cryofuture-*`, the four `fmi-*`.
 */
export type Grouping = 'alpha' | 'customer';

export interface Section {
  /** Scroll anchor and React key. */
  key: string;
  label: string;
  items: AppSummary[];
}

/**
 * Customer grouping is derived from the display name, because nothing else
 * carries it.
 *
 * Every one of the SafetyNet account's rows has `organizationId` set to XiltriX
 * North America itself — the customer whose site it is exists nowhere on the
 * row. `fleet` is no help either (111 of the 114 are on `x5RockyFleet`) and is
 * deliberately never sent to the browser. What is left is the naming
 * convention, which is consistent: the customer comes first and the site
 * follows, after a hyphen or a space.
 *
 * So this is a presentation convenience over a naming habit, not a real
 * relationship. Grouping a row wrongly costs a misplaced heading and nothing
 * else. If x5-admin ever links applications to customer organizations properly,
 * this should be replaced rather than extended.
 */
function tokenize(displayName: string): string[] {
  const stripped = displayName
    // `[VALIDATED]`, `[VALIDATION INSTANCE 04202026]` — a site marker, not part
    // of the customer's name.
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\b(validated|validation|instance)\b/gi, ' ');

  const tokens = stripped.split(/[^A-Za-z0-9]+/).filter(Boolean);
  return tokens.length > 0 ? tokens : displayName.split(/[^A-Za-z0-9]+/).filter(Boolean);
}

/**
 * Instance names are a mix of `64x-mammoth`, `Abiomed Danvers` and
 * `abiomed-peabody`, so anything that isn't a letter shares one `#` bucket at
 * the top — which is where `localeCompare` has already sorted it.
 */
function letterOf(value: string): string {
  const first = value.trim().charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : '#';
}

/**
 * The longest run of leading tokens every member shares, so a group of
 * `donor services - tampa` and `donor services - vegas` is headed
 * "donor services" rather than "donor".
 */
function commonLabel(members: string[][]): string {
  const shortest = Math.min(...members.map((tokens) => tokens.length));
  const label: string[] = [];

  for (let i = 0; i < shortest; i++) {
    const values = new Set(members.map((tokens) => tokens[i]?.toLowerCase()));
    if (values.size !== 1) break;
    const token = members[0]?.[i];
    if (token) label.push(token);
  }

  return label.length > 0 ? label.join(' ') : (members[0]?.[0] ?? '');
}

export function buildSections(apps: AppSummary[], grouping: Grouping): Section[] {
  if (grouping === 'alpha') {
    // The API sorts by displayName, so first-seen order is alphabetical order
    // and the map needs no second sort.
    const byLetter = new Map<string, AppSummary[]>();
    for (const app of apps) {
      const key = letterOf(app.displayName);
      const bucket = byLetter.get(key);
      if (bucket) bucket.push(app);
      else byLetter.set(key, [app]);
    }
    return [...byLetter].map(([key, items]) => ({ key, label: key, items }));
  }

  const byCustomer = new Map<string, { tokens: string[][]; items: AppSummary[] }>();
  for (const app of apps) {
    const tokens = tokenize(app.displayName);
    const key = (tokens[0] ?? app.displayName).toLowerCase();
    const bucket = byCustomer.get(key);
    if (bucket) {
      bucket.tokens.push(tokens);
      bucket.items.push(app);
    } else {
      byCustomer.set(key, { tokens: [tokens], items: [app] });
    }
  }

  return [...byCustomer]
    .map(([key, { tokens, items }]) => ({
      key,
      // A lone instance is its own customer, so its heading would just repeat
      // the row. The label is still needed to sort and to drive the jump rail.
      label: items.length > 1 ? commonLabel(tokens) : (items[0]?.displayName ?? key),
      items,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/**
 * Jump targets for the A-Z rail: the first section under each initial.
 *
 * Sections are already in label order in both modes, so taking the first of
 * each initial keeps the rail monotonic — which is what makes it usable when
 * the sections are customers rather than single letters.
 */
export function railTargets(sections: Section[]): { letter: string; key: string }[] {
  const seen = new Map<string, string>();
  for (const section of sections) {
    const letter = letterOf(section.label);
    if (!seen.has(letter)) seen.set(letter, section.key);
  }
  return [...seen].map(([letter, key]) => ({ letter, key }));
}

const STORAGE_KEY = 'x5.instanceGrouping';
const GROUPINGS = ['alpha', 'customer'] as const;

export function readStoredGrouping(): Grouping | null {
  return readPref(STORAGE_KEY, GROUPINGS);
}

export function storeGrouping(grouping: Grouping): void {
  writePref(STORAGE_KEY, grouping);
}
